import { Link } from "react-router-dom";
import { Heart, Phone, MapPin, Calendar, Shield, ArrowRight, ArrowUpRight } from "lucide-react";
import SEOHead from "../components/SEOHead";

// All editorial copy is original. Only verifiable facts about Childhelp are
// preserved (EIN, hotline number, 1959 founding year, hotline language coverage,
// hotline call volume estimate). Childhelp logo URLs are referenced directly
// from childhelp.org per their public partner page.
const PARTNERS = [
    { name: "Johnson & Johnson", logo: "https://childhelp.org/wp-content/uploads/2024/12/Johnson-Johnson_Logo_1.png", url: "https://www.jnj.com/" },
    { name: "Google", logo: "https://childhelp.org/wp-content/uploads/2024/12/Google.png", url: "https://about.google/" },
    { name: "Pacific Life", logo: "https://childhelp.org/wp-content/uploads/2024/12/Pacific-Life_idkVY6TI9R_0.png", url: "https://www.pacificlife.com/" },
    { name: "In-N-Out Burger", logo: "https://childhelp.org/wp-content/uploads/2024/12/In-N-Out-Burger.webp", url: "https://www.in-n-out.com/" },
    { name: "Tito's Handmade Vodka", logo: "https://childhelp.org/wp-content/uploads/2024/12/titos-new.webp", url: "https://www.titosvodka.com/" },
    { name: "Kendra Scott", logo: "https://childhelp.org/wp-content/uploads/2024/12/kendra-scott-copy.webp", url: "https://www.kendrascott.com/" },
    { name: "Blue Cross Blue Shield", logo: "https://childhelp.org/wp-content/uploads/2024/12/bcbs.png", url: "https://www.bcbs.com/" },
    { name: "Penn Mutual", logo: "https://childhelp.org/wp-content/uploads/2024/12/penn-mutual.webp", url: "https://www.pennmutual.com/" },
    { name: "Shamrock Foods", logo: "https://childhelp.org/wp-content/uploads/2024/12/ShamrockFoods.webp", url: "https://www.shamrockfoodservice.com/" },
    { name: "Barrett-Jackson", logo: "https://childhelp.org/wp-content/uploads/2024/12/barrett-jackson.png", url: "https://www.barrett-jackson.com/" },
    { name: "Grimaldi's", logo: "https://childhelp.org/wp-content/uploads/2024/12/grimaldis-copy.webp", url: "https://www.grimaldispizzeria.com/" },
    { name: "Streets of NY", logo: "https://childhelp.org/wp-content/uploads/2024/12/streets-of-ny.png", url: "https://streetsofnewyork.com/" },
];

const FAQ = [
    {
        q: "When is the next backpack drive at ZenZest?",
        a: "Every month, the third week (Monday–Saturday), at ZenZest Queens — 272-06 Union Turnpike. Walk in any time during open hours during that window; the collection bin sits at the front desk.",
    },
    {
        q: "What can I drop off?",
        a: "New backpacks of any size, notebooks, pens and pencils, hygiene kits (toothbrush, toothpaste, deodorant, soap), socks, underwear, and gift cards (Target, Walmart, Amazon). Childhelp routes everything through their residential, foster, and advocacy programs.",
    },
    {
        q: "Is my donation tax-deductible?",
        a: "Yes. Childhelp, Inc. is a registered 501(c)(3) public charity (Federal Tax ID 95-2884608). Keep your itemized receipt and check with your tax advisor on what to claim.",
    },
    {
        q: "Do I have to be a ZenZest customer?",
        a: "Not at all. Anyone can walk in during the drive window and drop off supplies — no ID, no purchase, no 21+ requirement. The bin is at the front desk during all open hours.",
    },
    {
        q: "Can I call the Childhelp hotline anonymously?",
        a: "Yes. The 24/7 Childhelp National Child Abuse Hotline at 1-800-422-4453 is free, confidential, and anonymous. Professional counselors answer in 170+ languages. If a child is in immediate danger, dial 911 first.",
    },
];

export default function Community() {
    return (
        <>
            <SEOHead
                title="Community Support · ZenZest's Childhelp Partnership — Queens & Staten Island"
                description="ZenZest hosts monthly Childhelp backpack drives at our Queens flagship — supporting abused, neglected, and at-risk children across New York. 501(c)(3) partner."
                canonical="https://weeddispensarynearme.com/community"
            />

            {/* ============ HERO ============ */}
            <section data-testid="community-hero" className="bg-zen-bg">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5 inline-flex items-center gap-1.5">
                            <Heart size={11} /> Community support
                        </p>
                        <h1
                            data-testid="community-h1"
                            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-zen-ink leading-[1.05] tracking-tight mb-7"
                        >
                            Standing with <span className="italic text-zen-olive">Childhelp.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-zen-ink-soft max-w-xl mb-9 leading-relaxed">
                            ZenZest partners with{" "}
                            <a
                                href="https://childhelp.org/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 text-zen-ink hover:text-zen-olive"
                            >
                                Childhelp
                            </a>
                            , a national nonprofit that meets the physical, emotional, and
                            educational needs of children who&rsquo;ve been abused,
                            neglected, or are otherwise at risk. Every customer who walks
                            through our door helps that work continue.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="https://childhelp.org/donate/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="community-donate-cta"
                                className="bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center gap-2"
                            >
                                Donate to Childhelp <ArrowRight size={13} />
                            </a>
                            <Link
                                to="/queens/contact"
                                className="border border-zen-ink text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-ink hover:text-zen-cream transition-colors"
                            >
                                Partner with ZenZest
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-zen-elevated">
                            <img
                                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1400&q=80"
                                alt="Children walking with backpacks — ZenZest's monthly Childhelp backpack drive"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ FACTS STRIP ============ */}
            <section className="bg-zen-surface border-y border-zen-line">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-px bg-zen-line">
                    <FactCard
                        icon={<Phone size={18} className="text-zen-olive" />}
                        eyebrow="National Child Abuse Hotline"
                        value="1-800-422-4453"
                        sub="24/7 · confidential · 170+ languages. If a child is in immediate danger, call 911 first."
                        href="tel:18004224453"
                    />
                    <FactCard
                        icon={<Calendar size={18} className="text-zen-olive" />}
                        eyebrow="Next drive at ZenZest"
                        value="3rd week, Mon–Sat"
                        sub="Hosted at ZenZest Queens — 272-06 Union Turnpike. Walk in any time during open hours."
                    />
                    <FactCard
                        icon={<Shield size={18} className="text-zen-olive" />}
                        eyebrow="501(c)(3) status"
                        value="Registered nonprofit"
                        sub="Childhelp, Inc. — Federal Tax ID (EIN) 95-2884608. Donations tax-deductible to the fullest extent allowed."
                    />
                    <FactCard
                        icon={<MapPin size={18} className="text-zen-olive" />}
                        eyebrow="Coverage on-site"
                        value="Fully insured"
                        sub="Every drive at our Queens store is covered under our commercial general liability and Childhelp's national event policy."
                    />
                </div>
            </section>

            {/* ============ THEIR MISSION ============ */}
            <section className="bg-zen-bg">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        Their mission
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-8 max-w-3xl">
                        For every child, a chance to be a child.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-zen-ink-soft text-sm sm:text-base leading-relaxed">
                        <div className="space-y-4">
                            <p>
                                Founded in 1959, Childhelp is one of the oldest and largest
                                nonprofits in the United States focused on the prevention,
                                intervention, and treatment of child abuse. Their work meets
                                kids where they are — and works upstream so fewer of them
                                ever need it.
                            </p>
                            <p>
                                Residential treatment villages, foster care and adoption
                                programs, advocacy centers, group homes, therapeutic schools,
                                and prevention curricula reach children in nearly every U.S.
                                state. The 24/7 National Child Abuse Hotline alone has
                                answered more than 11 million calls.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <p>
                                For our team in New York, the partnership is personal. We
                                live in the same neighborhoods we serve. The same kids on the
                                same blocks are the ones Childhelp protects every day.
                                We&rsquo;d rather build that community alongside our
                                neighbors than around them.
                            </p>
                            <p>
                                Hosting a drive in a dispensary is unusual on purpose.
                                Cannabis retail in New York is brand new — and we&rsquo;d
                                like the next generation of New Yorkers to grow up in a city
                                where legal cannabis businesses pull their weight on the
                                things that matter.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PARTNER NETWORK ============ */}
            <section data-testid="community-partners" className="bg-zen-surface border-y border-zen-line">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        Childhelp&rsquo;s national partner network
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        In trusted company.
                    </h2>
                    <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl mb-6 leading-relaxed">
                        Childhelp is backed by some of the most recognizable companies in
                        the country. ZenZest joins as a New York-local supporter, hosting
                        monthly drives out of our Queens flagship.{" "}
                        <a
                            href="mailto:contact@zenzest.com?subject=Childhelp%20Partnership"
                            className="underline underline-offset-2 text-zen-ink hover:text-zen-olive"
                        >
                            Want your business to join the next drive?
                        </a>
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-zen-line">
                        {PARTNERS.map((p) => (
                            <a
                                key={p.name}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={p.name}
                                className="bg-zen-bg h-24 sm:h-28 flex items-center justify-center p-5 hover:bg-zen-elevated/5 transition-colors group"
                            >
                                <img
                                    src={p.logo}
                                    alt={p.name}
                                    className="max-h-12 max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                                    loading="lazy"
                                />
                            </a>
                        ))}
                    </div>
                    <p className="text-xs text-zen-ink-soft mt-6 max-w-2xl leading-relaxed">
                        Logos shown are Childhelp&rsquo;s national corporate partners as
                        listed on{" "}
                        <a
                            href="https://childhelp.org/ways-to-give/corporate-partnerships/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-zen-olive"
                        >
                            childhelp.org/ways-to-give/corporate-partnerships
                        </a>
                        . ZenZest is an independent NY-local supporter and is not affiliated
                        with the companies above.
                    </p>
                </div>
            </section>

            {/* ============ HOW IT WORKS ============ */}
            <section data-testid="community-how" className="bg-zen-bg">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        How it works
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-8 max-w-3xl">
                        Drives on the calendar, not in a back office.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                            {
                                step: "01",
                                title: "We host the drive",
                                body: "The third week of every month — Monday through Saturday — our Queens store on Union Tpke turns its lobby into a Childhelp collection point. Drop-off bins live at the front desk during open hours.",
                            },
                            {
                                step: "02",
                                title: "Neighbors fill backpacks",
                                body: "Customers and walk-in neighbors bring new backpacks, school supplies, hygiene kits, and gift cards. The most-needed-items list lives at the door and online.",
                            },
                            {
                                step: "03",
                                title: "Childhelp delivers care",
                                body: "Childhelp picks up the donations and routes them through their residential, foster, and advocacy programs so they reach kids directly — fast and dignified.",
                            },
                        ].map((s) => (
                            <div
                                key={s.step}
                                data-testid={`community-step-${s.step}`}
                                className="p-7 bg-zen-surface border-l-2 border-zen-olive"
                            >
                                <p className="font-serif text-4xl text-zen-olive font-light leading-none mb-5">
                                    {s.step}
                                </p>
                                <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-zen-ink-soft leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FAQ ============ */}
            <section data-testid="community-faq" className="bg-zen-surface border-y border-zen-line">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        FAQ
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        Common questions about the drive.
                    </h2>
                    <div className="space-y-1">
                        {FAQ.map((item, i) => (
                            <details
                                key={i}
                                data-testid={`community-faq-${i}`}
                                className="group border-b border-zen-line bg-zen-bg [&_summary::-webkit-details-marker]:hidden"
                            >
                                <summary className="cursor-pointer px-5 py-5 flex items-center justify-between gap-4 text-left">
                                    <span className="font-serif text-lg text-zen-ink font-light leading-snug">
                                        {item.q}
                                    </span>
                                    <span className="text-zen-olive shrink-0 transition-transform group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <div className="px-5 pb-6 text-sm text-zen-ink-soft leading-relaxed max-w-3xl">
                                    {item.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ CTA ============ */}
            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-10 sm:py-14 text-center">
                    <p className="text-[11px] uppercase tracking-[0.3em] opacity-70 mb-4">
                        Get involved
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-8 max-w-3xl mx-auto">
                        Give a kid a softer landing.
                    </h2>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a
                            href="https://childhelp.org/donate/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-zen-cream text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-bg transition-colors inline-flex items-center gap-2"
                        >
                            Donate to Childhelp <ArrowUpRight size={13} />
                        </a>
                        <a
                            href="https://childhelp.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="border border-zen-cream/60 text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-cream hover:text-zen-ink transition-colors inline-flex items-center gap-2"
                        >
                            Learn more <ArrowUpRight size={13} />
                        </a>
                        <Link
                            to="/queens/contact"
                            className="border border-zen-cream/60 text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-cream hover:text-zen-ink transition-colors"
                        >
                            Partner with ZenZest
                        </Link>
                    </div>
                    <p className="text-xs opacity-60 mt-8">
                        Childhelp, Inc. is a registered 501(c)(3) public charity. EIN 95-2884608.
                    </p>
                </div>
            </section>
        </>
    );
}

function FactCard({ icon, eyebrow, value, sub, href }) {
    const inner = (
        <>
            <div className="mb-4">{icon}</div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                {eyebrow}
            </p>
            <p className="font-serif text-2xl text-zen-ink font-light leading-tight mb-3">
                {value}
            </p>
            <p className="text-xs text-zen-ink-soft leading-relaxed">{sub}</p>
        </>
    );
    return href ? (
        <a href={href} className="block bg-zen-bg p-7 hover:bg-zen-bg/80 transition-colors">
            {inner}
        </a>
    ) : (
        <div className="bg-zen-bg p-7">{inner}</div>
    );
}
